import { DownArrowIcon, PlusInSquareIcon, TrashAltIcon } from "assets/icons";
import { Media } from "components";
import { useState } from "react";
import {
  Controller,
  useFieldArray,
  useFormContext,
  useWatch
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Button, IconButton, Input } from "ui";

import { PackFormValues, PackSlotOptionFormValue } from "../../packs.types";
import { PackVariantsPickerModal } from "../modals";

import styles from "./PackForm.module.scss";

const SLOT_TITLE_EXAMPLES = ["Box", "Chain", "Pendant", "Earrings"];

const getSlotTitlePlaceholder = (
  t: (key: string, options?: Record<string, unknown>) => string,
  index: number
): string => {
  const example = SLOT_TITLE_EXAMPLES[index];
  return example
    ? t("e.g. {{example}}", { example: t(example) })
    : t("Slot title");
};

export const PackSlotsEditor = () => {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext<PackFormValues>();
  const slotsValue = useWatch({ control, name: "slots" });

  const { fields, append, remove, move } = useFieldArray({
    control,
    name: "slots"
  });

  const [editingSlotIndex, setEditingSlotIndex] = useState<number | null>(null);

  const removeSlotOption = (slotIndex: number, optionIndex: number) => {
    const current = slotsValue?.[slotIndex]?.options ?? [];
    const next: PackSlotOptionFormValue[] = current
      .filter((_, idx) => idx !== optionIndex)
      .map((option, idx) => ({ ...option, position: idx }));
    setValue(`slots.${slotIndex}.options`, next, { shouldDirty: true });
  };

  return (
    <div className={styles.packForm__block}>
      <h4 className={styles.packForm__blockTitle}>{t("Slots")}</h4>

      <p className={styles.packForm__hint}>
        {t(
          "Each slot is a step the customer fills with one variant from your curated list. Component prices are ignored — the pack uses the fixed pack price above."
        )}
      </p>

      {fields.map((field, index) => {
        const slotOptions = slotsValue?.[index]?.options ?? [];
        return (
          <div className={styles.packForm__slot} key={field.id}>
            <div className={styles.packForm__slotHeader}>
              <span className={styles.packForm__slotIndex}>
                {t("Slot")} #{index + 1}
              </span>
              <div className={styles.packForm__slotControls}>
                <IconButton
                  disabled={index === 0}
                  onClick={() => move(index, index - 1)}
                  icon={
                    <DownArrowIcon
                      fill={"#090914"}
                      width={18}
                      height={18}
                      style={{ transform: "rotate(180deg)" }}
                    />
                  }
                />
                <IconButton
                  disabled={index === fields.length - 1}
                  onClick={() => move(index, index + 1)}
                  icon={
                    <DownArrowIcon fill={"#090914"} width={18} height={18} />
                  }
                />
                <IconButton
                  disabled={fields.length === 1}
                  onClick={() => remove(index)}
                  icon={
                    <TrashAltIcon fill={"#FF0033"} width={18} height={18} />
                  }
                />
              </div>
            </div>

            <Controller
              control={control}
              name={`slots.${index}.title`}
              render={({ field: titleField, fieldState: { error } }) => (
                <Input
                  value={titleField.value}
                  onChange={titleField.onChange}
                  label={t("Slot title")}
                  placeholder={getSlotTitlePlaceholder(t, index)}
                  errorMessage={error?.message}
                  maxLength={120}
                />
              )}
            />

            <div className={styles.packForm__slotOptions}>
              {slotOptions.length === 0 ? (
                <p className={styles.packForm__hint}>
                  {t("No variants selected yet")}
                </p>
              ) : (
                slotOptions.map((option, optionIndex) => (
                  <div
                    className={styles.packForm__slotOption}
                    key={`${option.productVariantId}-${optionIndex}`}
                  >
                    {option.productVariantImage ? (
                      <Media
                        className={styles.packForm__slotOptionThumb}
                        mediaFile={option.productVariantImage}
                      />
                    ) : (
                      <div className={styles.packForm__slotOptionThumb} />
                    )}
                    <div className={styles.packForm__slotOptionInfo}>
                      <div className={styles.packForm__slotOptionName}>
                        {option.productVariantName || option.productVariantId}
                      </div>
                      {(option.productCategoryName ||
                        option.productColorValue) && (
                        <div className={styles.packForm__slotOptionSubtitle}>
                          {option.productCategoryName && (
                            <span>{option.productCategoryName}</span>
                          )}
                          {option.productCategoryName &&
                            option.productColorValue && <span>·</span>}
                          {option.productColorValue && (
                            <>
                              <span
                                className={styles.packForm__slotOptionColorDot}
                                style={{
                                  backgroundColor: option.productColorValue
                                }}
                              />
                              <span>{t(option.productColorValue)}</span>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    <IconButton
                      onClick={() => removeSlotOption(index, optionIndex)}
                      icon={
                        <TrashAltIcon fill={"#FF0033"} width={18} height={18} />
                      }
                    />
                  </div>
                ))
              )}

              <Button
                variant="default"
                onClick={() => setEditingSlotIndex(index)}
                icon={<PlusInSquareIcon stroke={"#FB3973"} />}
              >
                {slotOptions.length === 0
                  ? t("Add variants")
                  : t("Edit variants")}
              </Button>
            </div>
          </div>
        );
      })}

      <Button
        className={styles.packForm__addSlotButton}
        variant="default"
        onClick={() =>
          append({
            title: "",
            position: fields.length,
            options: []
          })
        }
        icon={<PlusInSquareIcon stroke={"#FB3973"} />}
      >
        {t("Add slot")}
      </Button>

      <Controller
        key={editingSlotIndex ?? "closed"}
        control={control}
        name={`slots.${editingSlotIndex ?? 0}.options`}
        render={({ field }) => (
          <PackVariantsPickerModal
            key={editingSlotIndex ?? "closed"}
            isOpen={editingSlotIndex !== null}
            onClose={() => setEditingSlotIndex(null)}
            value={field.value}
            onChange={field.onChange}
            title={t("Variants for slot ") + ((editingSlotIndex ?? 0) + 1)}
          />
        )}
      />
    </div>
  );
};
